/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { Button } from "src/business-types/grocery/components/Button";
import DropdownSelect from "src/business-types/grocery/components/DropdownSelect";
import InputBox from "src/business-types/grocery/components/InputBox";
import PopupModules from "src/features/auth/components/PopupModules";
import { UOMaddInterface } from "src/shared/services/interface";
import { typeMeasurement, typeMinimumMeasurement } from "./unitFactors";

export type UOMInter = {
  uomName: string;
  baseUnit: string;
  conversionFactor: number | string;
};

function AddUOM({ isOpen, onClose, post, formData, setFormData }: UOMaddInterface) {
  const [baseUnit, setBaseUnit] = useState<boolean>(false);
  const [qty, setQty] = useState<boolean>(false);

  const handleChange = (field: keyof UOMInter, value: string | number) => {
    // Limit UOM name length
    if (field === "uomName" && typeof value === "string" && value.length > 20) return;

    setFormData((prev: any) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleConfirm = async () => {
    await post({
      ...formData,
      conversionFactor: Number(formData.conversionFactor),
    });
  };

  // -------------------------------------------
  // 🔥 AUTO DETECT UOM (match alias + lowercase)
  // -------------------------------------------
  useEffect(() => {
    const value = formData.uom_name?.trim().toLowerCase();

    if (!value) {
      resetUom();
      return;
    }

    const match = typeMeasurement.find((item) => {
      const main = item.uom_name.toLowerCase();
      const aliases = item.aliases?.map((a) => a.toLowerCase()) ?? [];
      const patterns = item.patterns?.map((p) => p.toLowerCase()) ?? [];

      return main === value || aliases.includes(value) || aliases.some((a) => value.includes(a) || a.includes(value)) || patterns.some((p) => value.includes(p));
    });

    if (!match) {
      // ❌ Don't reset aggressively → better UX
      setBaseUnit(false);
      setQty(false);
      return;
    }

    // ✅ Valid match
    setFormData((prev: any) => {
      if (prev.baseUnit === match.val && prev.conversionFactor === String(match.factor)) {
        return prev;
      }

      return {
        ...prev,
        baseUnit: match.val,
        conversionFactor: String(match.factor),
      };
    });

    setBaseUnit(true);
    setQty(match.factor > 0);
  }, [formData.uom_name]);

  // -------------------------------------------
  // RESET HELPERS
  // -------------------------------------------
  const resetUom = () => {
    setFormData((prev: UOMInter) => ({
      ...prev,
      uomName: "",
      baseUnit: "",
      conversionFactor: "",
    }));
    setBaseUnit(false);
    setQty(false);
  };

  const fullResetUom = () => {
    setFormData({
      uomName: "",
      baseUnit: "",
      conversionFactor: "",
    });
    setBaseUnit(false);
    setQty(false);
  };

  // -------------------------------------------
  // RETURN UI
  // -------------------------------------------
  return (
    <PopupModules
      isOpen={!!isOpen}
      onClose={() => {
        onClose();
        fullResetUom();
      }}
      title='Add Unit Of Measurement'
    >
      <div className='w-full h-full'>
        {/* UNIT TYPE */}
        <p className='px-1 text-black font-semibold'>Unit Type</p>

        <InputBox placeholder='Unit Name' value={formData.uomName} onChange={(e: any) => handleChange("uomName", e.target.value)} />

        {/* BASE UNIT */}
        <DropdownSelect
          title='Base Unit'
          name='baseUnit'
          value={formData.baseUnit}
          options={typeMinimumMeasurement.map((c) => ({ _id: c.val, CategoryName: c.uom_name }))}
          onChange={(e) => {
            setFormData({ ...formData, baseUnit: e });
          }}
          className='mt-2'
          disabled={baseUnit}
        />

        {/* CONVERSION FACTOR */}
        <p className='px-1 mt-2 text-black font-semibold'>Qty (in Base Unit)</p>
        <InputBox
          type='number'
          placeholder='Enter number'
          value={formData.conversionFactor}
          min={0}
          max={100000}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange("conversionFactor", e.target.value)}
          disabled={qty}
          error={Number(formData.conversionFactor) < 0}
        />

        {formData.conversion_factor !== "" && Number(formData.conversion_factor) < 0 && <p className='text-red-500 text-sm'>Enter valid number</p>}

        {/* BUTTONS */}
        <div className='flex justify-center items-center mt-4 gap-4'>
          <Button
            variant='ThemeCancel'
            label='Cancel'
            className='border'
            onClick={() => {
              onClose();
              fullResetUom();
            }}
          />
          <Button
            variant='ThemeBase'
            label='Confirm'
            onClick={handleConfirm}
          // disabled={!formData.uomName || !formData.baseUnit || !formData.conversionFactor}
          />
        </div>
      </div>
    </PopupModules>
  );
}

export default AddUOM;
